<template>
  <Popover
    position="is-bottom-left"
    ref="popover"
    class="has-text-weight-medium"
    @on-active-change="onPopoverActiveChange"
    :data-e2e="TimeEntryCypressAttribute.buttons.ADD_TIME_ENTRY_POPOVER"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <template #content>
      <div
        v-if="isReadOnly && timeEntryUsers.count === 0"
        class="p-4 has-text-secondary-grey is-size-5"
      >
        {{ noTimeTrackedText }}
      </div>
      <div v-else>
        <section class="rows">
          <div v-for="user in timeEntryUsers.get()" :key="user.id" class="row">
            <Collapse
              :open="false"
              :is-borderless="true"
              :has-borderless-header="false"
              :data-e2e="TimeEntryCypressAttribute.views.USER_COLLAPSE"
            >
              <template #header>
                <div
                  class="is-flex is-align-items-center is-justify-content-space-between is-full-width"
                >
                  <div class="user-collapse-header-identity is-flex is-align-items-center overflow-hidden">
                    <UserAvatar :user="user" />
                    <div
                      class="m-l-8 is-size-5 is-relative"
                      :data-e2e="TimeEntryCypressAttribute.views.USER_COLLAPSE_NAME"
                    >
                      {{ user.name }}
                      <p
                        v-if="user.deletedAt"
                        class="subtitle is-uppercase has-text-weight-semibold is-size-6 m-b-0"
                      >
                        {{ $t('core.deactivated') }}
                      </p>
                    </div>
                  </div>
                  <p
                    class="m-l-5 is-size-5 has-text-grey m-0 whitespace-no-wrap is-flex-shrink-0"
                    :data-e2e="TimeEntryCypressAttribute.views.TIME_ENTRY_ROW_DURATION"
                  >
                    {{ user.timeEntries.totalDurationFormatted }}
                  </p>
                </div>
              </template>
              <TimeEntryRows
                :time-entries="user.timeEntries"
                v-on="$listeners"
                @on-edit="onEdit"
                :is-read-only="isReadOnly && user.timeEntries.count === 0"
              />
            </Collapse>
          </div>
        </section>
      </div>
      <AddTimeEntryForm
        v-if="!isReadOnly || isEditing"
        :user-id="authUser.id"
        :type="type"
        :sub-type="subType"
        :initial-date="initialDate"
        :new-entry="newEntry"
        :week-dates="weekDates"
        :custom-entry="entry"
        @on-save="onSave"
        @on-save-completed="onSaveCompleted"
        @on-save-failed="onSaveFailed"
      />
    </template>
  </Popover>
</template>

<script>
import { cloneDeep } from 'lodash';
import Collapse from '@/modules/core/app/components/Collapse.vue';
import Popover from '@/modules/core/ui/components/Popover.vue';
import { UserCollection } from '@/modules/user/models/UserCollection';
import UserAvatar from '@/modules/user/components/presentational/UserAvatar.vue';
import TimeEntryRows from '@/modules/timeentry/components/presentational/TimeEntryRows.vue';
import AddTimeEntryForm from '@/modules/timeentry/components/stateful/AddTimeEntryForm.vue';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import { User } from '@/modules/user/models/User';
import { TimeEntryCypressAttribute } from '@/modules/timeentry/constants/TimeEntryCypressAttribute';
import { TimeEntry } from '@/modules/timeentry/models/TimeEntry';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'TimeEntryPopover',
  emits: ['on-save'],
  slotsDefinition: {
    trigger: {}
  },
  mixins: [HasAuthUserMixin],
  components: {
    AddTimeEntryForm,
    TimeEntryRows,
    UserAvatar,
    Popover,
    Collapse
  },
  props: {
    users: {
      type: UserCollection
    },
    type: {
      type: String,
      required: true
    },
    subType: {
      type: String,
      default: null
    },
    initialDate: {
      type: Date
    },
    newEntry: {
      type: TimeEntry
    },
    weekDates: {
      type: Array,
      default: () => []
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  static: {
    TimeEntryCypressAttribute
  },
  data() {
    return {
      isEditing: false,
      formDate: this.initialDate,
      entry: null
    };
  },
  computed: {
    timeEntryUsers() {
      return this.users;
    },
    noTimeTrackedText() {
      return this.$t('timeentry.no-time-tracked-yet', {
        entryType: this.$t(this.newEntry ? 'estate.task' : 'accounting.estate').toLowerCase()
      });
    }
  },
  methods: {
    getUser(id) {
      return this.timeEntryUsers.findByKey(id);
    },
    onSave(userId, timeEntry) {
      let user = this.getUser(userId);
      if (!user) {
        user = User.mapModel(cloneDeep(this.authUser));
        this.timeEntryUsers.add(user);
      }

      user.timeEntries.save(timeEntry);
    },
    onSaveCompleted(userId, timeEntry) {
      this.getUser(userId).timeEntries.updateByTempId(timeEntry);
      this.$emit('on-save', timeEntry);
      this.isEditing = false;
    },
    onSaveFailed(userId, timeEntry) {
      this.getUser(userId).timeEntries.delete(timeEntry.id, 'tempId');
      this.isEditing = false;
    },
    onEdit(timeEntry) {
      this.isEditing = true;
      this.entry = timeEntry;
    },
    onPopoverActiveChange(value) {
      this.entry = value ? null : this.entry;
      this.isEditing = false;
    }
  }
};
</script>

<style scoped>
.user-collapse-header-identity {
  min-width: 0;
}

:deep(.dropdown-content) {
  padding: 0;
}

:deep(.collapse) {
  width: 100%;
  margin-bottom: 0 !important;
}

:deep(.collapse .collapse-content > .p-2) {
  padding: 0 !important;
}

:deep(div.trigger) {
  padding: var(--ce-5px) var(--ce-10px);
  position: relative;
}

:deep(div.trigger div.icon) {
  font-size: var(--ce-12px);
}

.rows {
  min-width: var(--ce-300px);
  max-height: var(--ce-300px);
  overflow-y: auto;

  > div.row {
    width: 100%;
    min-height: var(--ce-45px);
    margin-top: var(--ce-2px);
    border-bottom: 1px solid var(--ce-border);

    &.selected {
      background-color: var(--ce-primary-light);
    }
  }
}
</style>
